import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, X, Users, CheckCircle2, Circle, Clock, AlertCircle, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const PRIORITY_STYLE = {
  'High': 'bg-red-900/40 text-red-300 border-red-700',
  'Medium': 'bg-amber-900/40 text-amber-300 border-amber-700',
  'Low': 'bg-green-900/40 text-green-300 border-green-700',
};
const DEPARTMENTS = ['Production', 'Quality Control', 'Administration', 'Logistics', 'Sales', 'Finance'];

export default function Workforce() {
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [quickTaskEmp, setQuickTaskEmp] = useState(null);
  const [quickTaskForm, setQuickTaskForm] = useState({
    title: '', priority: 'Medium', due_date: '',
    // iter156 — One Tasks system, optional Recurring? toggle. Cadence_days=0
    // means one-time. Named presets resolve server-side too.
    recurring: false, cadence_days: 0, lead_time_days: 3, category: '',
  });
  const [empForm, setEmpForm] = useState({ name: '', role: '', phone: '', email: '', department: 'Production', salary: '' });
  const [submitting, setSubmitting] = useState(false);
  const [expandedEmp, setExpandedEmp] = useState(null);

  const fetchData = async () => {
    try {
      const [eRes, tRes] = await Promise.all([
        axios.get(`${API}/workforce/employees`, { withCredentials: true }),
        axios.get(`${API}/workforce/tasks`, { withCredentials: true }),
      ]);
      setEmployees(eRes.data); setTasks(tRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete, isAdmin } = useAdminDelete(fetchData);

  useEffect(() => { fetchData(); }, []);

  const updateTaskStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/workforce/tasks/${id}/status`, { status }, { withCredentials: true });
      setTasks(prev => prev.map(t => t.id === id ? res.data : t));
      if (status === 'Completed') toast.success('Task completed!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleTask = (task) => {
    updateTaskStatus(task.id, task.status === 'Completed' ? 'Pending' : 'Completed');
  };

  const handleAddEmp = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const res = await axios.post(`${API}/workforce/employees`, { ...empForm, salary: parseFloat(empForm.salary) || 0 }, { withCredentials: true });
      setEmployees(prev => [...prev, res.data]);
      setShowEmpModal(false);
      setEmpForm({ name: '', role: '', phone: '', email: '', department: 'Production', salary: '' });
      toast.success('Employee added!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const handleQuickTask = async (empId, empName) => {
    if (!quickTaskForm.title.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/workforce/tasks`, {
        ...quickTaskForm,
        assigned_to_id: empId,
        assigned_to: empName,
        description: '',
      }, { withCredentials: true });
      setTasks(prev => [...prev, res.data]);
      setQuickTaskEmp(null);
      setQuickTaskForm({ title: '', priority: 'Medium', due_date: '', recurring: false, cadence_days: 0, lead_time_days: 3, category: '' });
      toast.success(quickTaskForm.recurring ? 'Recurring task created — will auto-cycle on completion' : 'Task added!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const getEmpTasks = (empName) => tasks.filter(t => t.assigned_to === empName);
  const unassignedTasks = tasks.filter(t => !t.assigned_to || !employees.some(e => e.name === t.assigned_to));

  const totalSalary = employees.reduce((s, e) => s + (e.salary || 0), 0);
  const pendingTasks = tasks.filter(t => t.status !== 'Completed').length;
  const completedTasks = tasks.filter(t => t.status === 'Completed').length;

  if (loading) return <div className="text-zinc-400 animate-pulse text-sm p-8">Loading workforce...</div>;

  return (
    <div className="space-y-5" data-testid="workforce-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Workforce</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{employees.length} employees · {fmt(totalSalary)}/mo · {pendingTasks} pending · {completedTasks} done</p>
        </div>
        <button onClick={() => setShowEmpModal(true)} data-testid="add-employee-btn"
          className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl transition-colors">
          <Plus size={14} /> Add Employee
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center"><Users size={16} className="text-amber-400" /></div>
          <div><div className="text-zinc-500 text-[10px] uppercase tracking-wider">Team</div><div className="font-heading font-bold text-xl text-amber-400">{employees.length}</div></div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center"><Clock size={16} className="text-blue-400" /></div>
          <div><div className="text-zinc-500 text-[10px] uppercase tracking-wider">Pending</div><div className="font-heading font-bold text-xl text-blue-400">{pendingTasks}</div></div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center"><CheckCircle2 size={16} className="text-green-400" /></div>
          <div><div className="text-zinc-500 text-[10px] uppercase tracking-wider">Done</div><div className="font-heading font-bold text-xl text-green-400">{completedTasks}</div></div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center"><AlertCircle size={16} className="text-red-400" /></div>
          <div><div className="text-zinc-500 text-[10px] uppercase tracking-wider">Overdue</div><div className="font-heading font-bold text-xl text-red-400">{tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'Completed').length}</div></div>
        </div>
      </div>

      {/* ═══ EMPLOYEE CARDS WITH TASKS ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {employees.map(emp => {
          const empTasks = getEmpTasks(emp.name);
          const empPending = empTasks.filter(t => t.status !== 'Completed');
          const empDone = empTasks.filter(t => t.status === 'Completed');
          const att = emp.attendance_this_month;
          const attPct = att?.total > 0 ? (att.present / att.total * 100) : 0;
          const isExpanded = expandedEmp === emp.id;

          return (
            <div key={emp.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`emp-card-${emp.id}`}>
              {/* Employee header */}
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-heading font-bold text-sm flex-shrink-0">
                    {emp.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-heading font-semibold text-zinc-50 text-sm">{emp.name}</h3>
                        <p className="text-zinc-500 text-xs">{emp.role} · <span className="text-zinc-600">{emp.department}</span></p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-amber-400 text-xs font-semibold">{fmt(emp.salary)}/mo</span>
                        {isAdmin && <button onClick={() => requestDelete(`/workforce/employees/${emp.id}`, emp.name, `Delete employee "${emp.name}" and unassign their tasks.`)}
                          data-testid={`delete-emp-${emp.id}`}
                          className="p-1 rounded-lg text-zinc-700 hover:text-red-400 hover:bg-red-900/20 transition-all">
                          <Trash2 size={12} />
                        </button>}
                      </div>
                    </div>
                    {/* Mini attendance bar */}
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex-1 bg-zinc-800 rounded-full h-1">
                        <div className={`h-1 rounded-full ${attPct >= 90 ? 'bg-green-500' : attPct >= 80 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${attPct}%` }} />
                      </div>
                      <span className={`text-[10px] font-mono ${attPct >= 90 ? 'text-green-400' : attPct >= 80 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {att ? `${att.present}/${att.total}` : '—'}
                      </span>
                      <span className="text-zinc-600 text-[10px]">{emp.phone || ''}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tasks section */}
              <div className="border-t border-zinc-800/60">
                {/* Task count header */}
                <button onClick={() => setExpandedEmp(isExpanded ? null : emp.id)}
                  className="w-full flex items-center justify-between px-4 py-2 hover:bg-zinc-800/30 transition-colors"
                  data-testid={`emp-tasks-toggle-${emp.id}`}>
                  <span className="text-zinc-500 text-[10px] uppercase tracking-wider">
                    Tasks · <span className="text-zinc-300 font-bold">{empPending.length}</span> pending
                    {empDone.length > 0 && <span className="text-green-500"> · {empDone.length} done</span>}
                  </span>
                  {isExpanded ? <ChevronUp size={12} className="text-zinc-600" /> : <ChevronDown size={12} className="text-zinc-600" />}
                </button>

                {/* Always show pending tasks, expand to see completed */}
                <div className="px-3 pb-2 space-y-0.5">
                  {empPending.length === 0 && empDone.length === 0 && (
                    <p className="text-zinc-700 text-[10px] px-1 py-1">No tasks assigned</p>
                  )}

                  {/* Pending tasks — always visible */}
                  {empPending.map(task => (
                    <TaskRow key={task.id} task={task} isAdmin={isAdmin} onToggle={toggleTask} onDelete={(id, title) => requestDelete(`/workforce/tasks/${id}`, title, `Delete task "${title}".`)} />
                  ))}

                  {/* Completed tasks — only when expanded */}
                  {isExpanded && empDone.map(task => (
                    <TaskRow key={task.id} task={task} isAdmin={isAdmin} onToggle={toggleTask} onDelete={(id, title) => requestDelete(`/workforce/tasks/${id}`, title, `Delete task "${title}".`)} />
                  ))}

                  {/* Quick add — iter156: includes Recurring? toggle that
                      expands a small cadence picker inline. */}
                  {quickTaskEmp === emp.id ? (
                    <div className="space-y-1.5 mt-1 px-1" data-testid={`quick-task-form-${emp.id}`}>
                      <div className="flex items-center gap-1.5">
                        <input autoFocus value={quickTaskForm.title} onChange={e => setQuickTaskForm(f => ({ ...f, title: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) handleQuickTask(emp.id, emp.name); if (e.key === 'Escape') setQuickTaskEmp(null); }}
                          placeholder="Task title... (Enter to add)"
                          data-testid={`task-title-${emp.id}`}
                          className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-200 text-xs focus:outline-none focus:border-amber-500" />
                        <select value={quickTaskForm.priority} onChange={e => setQuickTaskForm(f => ({ ...f, priority: e.target.value }))}
                          className="bg-zinc-800 border border-zinc-700 rounded-lg px-1.5 py-1 text-zinc-400 text-[10px] focus:outline-none">
                          {['High', 'Medium', 'Low'].map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <input type="date" value={quickTaskForm.due_date} onChange={e => setQuickTaskForm(f => ({ ...f, due_date: e.target.value }))}
                          className="bg-zinc-800 border border-zinc-700 rounded-lg px-1.5 py-1 text-zinc-400 text-[10px] focus:outline-none w-28" />
                        <button onClick={() => handleQuickTask(emp.id, emp.name)} disabled={!quickTaskForm.title.trim() || submitting}
                          data-testid={`save-task-${emp.id}`}
                          className="text-[10px] text-amber-400 hover:text-amber-300 font-semibold disabled:opacity-40">Add</button>
                        <button onClick={() => setQuickTaskEmp(null)} className="text-zinc-600 hover:text-zinc-400">
                          <X size={12} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 text-[10px]">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="checkbox" checked={quickTaskForm.recurring}
                            onChange={e => setQuickTaskForm(f => ({ ...f, recurring: e.target.checked, cadence_days: e.target.checked ? (f.cadence_days || 30) : 0 }))}
                            data-testid={`recurring-toggle-${emp.id}`}
                            className="accent-amber-500" />
                          <span className="text-zinc-400">🔁 Recurring?</span>
                        </label>
                        {quickTaskForm.recurring && (
                          <>
                            <span className="text-zinc-600">repeat every</span>
                            <select value={quickTaskForm.cadence_days || 30}
                              onChange={e => setQuickTaskForm(f => ({ ...f, cadence_days: parseInt(e.target.value) || 0 }))}
                              data-testid={`cadence-${emp.id}`}
                              className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-zinc-300">
                              <option value={7}>weekly</option>
                              <option value={14}>biweekly</option>
                              <option value={30}>monthly</option>
                              <option value={90}>quarterly</option>
                              <option value={182}>half-yearly</option>
                              <option value={365}>yearly</option>
                            </select>
                            <span className="text-zinc-600">· remind</span>
                            <input type="number" min={0} max={30} value={quickTaskForm.lead_time_days}
                              onChange={e => setQuickTaskForm(f => ({ ...f, lead_time_days: parseInt(e.target.value) || 0 }))}
                              className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 w-12 text-zinc-300" />
                            <span className="text-zinc-600">days before</span>
                            <input value={quickTaskForm.category}
                              onChange={e => setQuickTaskForm(f => ({ ...f, category: e.target.value }))}
                              placeholder="category (optional)"
                              className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-300 w-36" />
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => { setQuickTaskEmp(emp.id); setQuickTaskForm({ title: '', priority: 'Medium', due_date: '', recurring: false, cadence_days: 0, lead_time_days: 3, category: '' }); }}
                      data-testid={`add-task-${emp.id}`}
                      className="flex items-center gap-1 text-zinc-600 hover:text-amber-400 text-[10px] px-1 py-1 transition-colors">
                      <Plus size={10} /> Add task
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ═══ UNASSIGNED TASKS ═══ */}
      {unassignedTasks.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center gap-2">
            <AlertCircle size={13} className="text-zinc-500" />
            <span className="text-zinc-400 text-xs font-medium">Unassigned Tasks</span>
            <span className="text-zinc-600 text-[10px] font-mono">{unassignedTasks.length}</span>
          </div>
          <div className="px-3 py-2 space-y-0.5">
            {unassignedTasks.map(task => (
              <TaskRow key={task.id} task={task} isAdmin={isAdmin} onToggle={toggleTask} onDelete={(id, title) => requestDelete(`/workforce/tasks/${id}`, title, `Delete task "${title}".`)} />
            ))}
          </div>
        </div>
      )}

      {/* ═══ ADD EMPLOYEE MODAL ═══ */}
      {showEmpModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50">Add Employee</h3>
              <button onClick={() => setShowEmpModal(false)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleAddEmp} className="p-5 space-y-3">
              {[['name', 'Full Name *', 'text'], ['role', 'Job Role *', 'text'], ['phone', 'Phone', 'tel'], ['email', 'Email', 'email'], ['salary', 'Monthly Salary', 'number']].map(([field, label, type]) => (
                <div key={field}>
                  <label className="text-zinc-400 text-xs mb-1 block">{label}</label>
                  <input required={['name', 'role'].includes(field)} type={type} value={empForm[field]} onChange={e => setEmpForm(f => ({ ...f, [field]: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
              ))}
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Department</label>
                <select value={empForm.department} onChange={e => setEmpForm(f => ({ ...f, department: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                  {DEPARTMENTS.map(d => <option key={d} value={d} className="bg-zinc-900">{d}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowEmpModal(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50">{submitting ? 'Saving...' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />
    </div>
  );
}

/* ─── Inline Task Row ─── */
function TaskRow({ task, onToggle, onDelete, isAdmin }) {
  const isDone = task.status === 'Completed';
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !isDone;

  return (
    <div className={`group flex items-center gap-2 px-1 py-1 rounded-lg transition-all hover:bg-zinc-800/40
      ${isDone ? 'opacity-50' : isOverdue ? 'bg-red-950/10' : ''}`}
      data-testid={`wf-task-${task.id}`}>
      {/* Checkbox */}
      <button onClick={() => onToggle(task)} data-testid={`wf-task-check-${task.id}`}
        className={`flex-shrink-0 w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center transition-all
          ${isDone ? 'border-green-500 bg-green-500/20' : 'border-zinc-600 hover:border-amber-500'}`}>
        {isDone && <CheckCircle2 size={10} className="text-green-400" />}
      </button>

      {/* Title */}
      <span className={`flex-1 text-xs leading-tight truncate ${isDone ? 'line-through text-zinc-600' : 'text-zinc-300'}`}>
        {task.title}
      </span>

      {/* iter156 — Recurring badge. Renders 🔁 + cadence so the operator
          knows this task auto-cycles on completion. */}
      {task.recurring && task.cadence_days > 0 && (
        <span title={`Repeats every ${task.cadence_days} day(s)`}
          data-testid={`recurring-badge-${task.id}`}
          className="text-[9px] px-1.5 py-0 rounded border bg-sky-500/10 text-sky-300 border-sky-700/40">
          🔁 {task.cadence_days < 14 ? `${task.cadence_days}d` : task.cadence_days === 30 ? 'M' : task.cadence_days === 90 ? 'Q' : task.cadence_days === 182 ? '6M' : task.cadence_days === 365 ? 'Y' : `${task.cadence_days}d`}
        </span>
      )}
      {task.category && (
        <span className="text-[9px] px-1 py-0 rounded bg-zinc-800 text-zinc-500 border border-zinc-700">{task.category}</span>
      )}

      {/* Priority dot */}
      <span className={`text-[9px] px-1.5 py-0 rounded border ${PRIORITY_STYLE[task.priority] || 'bg-zinc-800 text-zinc-500 border-zinc-700'}`}>
        {task.priority?.charAt(0)}
      </span>

      {/* Due date */}
      {task.due_date && (
        <span className={`text-[9px] font-mono ${isOverdue ? 'text-red-400' : 'text-zinc-600'}`}>
          {task.due_date?.slice(5, 10)}
        </span>
      )}

      {/* Delete — admin only */}
      {isAdmin && <button onClick={() => onDelete(task.id, task.title)} data-testid={`delete-wf-task-${task.id}`}
        className="p-0.5 rounded text-zinc-800 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
        <Trash2 size={10} />
      </button>}
    </div>
  );
}
